import React from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';

import { currency } from 'globals/currency';
import { fixDecimal } from 'utils/general';
import { generateColumn } from '../common';

type ProductStatisticsTableProps = {
  products: {
    name: string;
    amount: number;
    price: number;
  }[];
};

const ProductStatisticsTable: React.FC<ProductStatisticsTableProps> = ({
  products,
}) => {
  const data = React.useMemo(() => products, [products]);

  const columns = React.useMemo<ColumnDef<any, unknown>[]>(
    () => [
      generateColumn('name', {
        customHeader: i18next.t('product-statistics-table.product-name'),
      }),
      generateColumn('amount', {
        customHeader: i18next.t('product-statistics-table.ordered-times'),
      }),
      generateColumn('price', {
        customHeader: i18next.t('product-statistics-table.generated-turnover'),
        customCellFormatter: (props: any) => (
          <div>
            {fixDecimal(props.getValue())} {currency}
          </div>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <table className="table">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                onClick={header.column.getToggleSortingHandler()}
                style={{ cursor: 'pointer' }}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                <span>
                  {header.column.getIsSorted()
                    ? header.column.getIsSorted() === 'desc'
                      ? ' 🔽'
                      : ' 🔼'
                    : ''}
                </span>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

ProductStatisticsTable.propTypes = {
  products: PropTypes.array.isRequired,
};

export default ProductStatisticsTable;
